import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { generateM2Code, generateLoginKey } from "../../mocks/users";

interface RegisterFormData {
  name: string;
  phone: string;
  email: string;
  company: string;
  industry: string;
  city: string;
  agreeTerms: boolean;
}

const INDUSTRIES = [
  "Sản xuất", "Thương mại", "Công nghệ", "Xuất nhập khẩu", "Nội thất",
  "Xây dựng", "Y tế", "Giáo dục", "Vận tải", "Năng lượng",
  "Nông nghiệp", "Thực phẩm & Đồ uống", "Bất động sản", "Tài chính", "Khác",
];

const CITIES = [
  "Hà Nội", "Hồ Chí Minh", "Đà Nẵng", "Hải Phòng", "Cần Thơ",
  "Bình Dương", "Đồng Nai", "Long An", "Bà Rịa - Vũng Tàu", "Khác",
];

const RegisterPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<"form" | "success">("form");
  const [generatedCode, setGeneratedCode] = useState("");
  const [loginKey, setLoginKey] = useState("");
  const [errors, setErrors] = useState<Partial<RegisterFormData>>({});
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState<RegisterFormData>({
    name: "",
    phone: "",
    email: "",
    company: "",
    industry: "",
    city: "",
    agreeTerms: false,
  });

  const update = (field: keyof RegisterFormData, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = (): boolean => {
    const newErrors: Partial<RegisterFormData> = {};
    if (!form.name.trim() || form.name.trim().split(" ").length < 2)
      (newErrors as Record<string, string>)["name"] = "Vui lòng nhập đầy đủ họ tên";
    if (!form.phone.trim() || !/^0[0-9]{9}$/.test(form.phone.trim()))
      (newErrors as Record<string, string>)["phone"] = "Số điện thoại không hợp lệ (10 số, bắt đầu bằng 0)";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      (newErrors as Record<string, string>)["email"] = "Email không hợp lệ";
    if (!form.industry) (newErrors as Record<string, string>)["industry"] = "Vui lòng chọn ngành nghề";
    if (!form.city) (newErrors as Record<string, string>)["city"] = "Vui lòng chọn thành phố";
    if (!form.agreeTerms) (newErrors as Record<string, string>)["agreeTerms"] = "Vui lòng đồng ý điều khoản";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setTimeout(() => {
      const code = generateM2Code(form.phone, form.name);
      const key = generateLoginKey(form.phone, form.name);
      setGeneratedCode(code);
      setLoginKey(key);
      setStep("success");
      setLoading(false);
    }, 1000);
  };

  if (step === "success") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8F9FA] via-white to-[#EDF2FB] flex flex-col">
        <div className="bg-[#004A99] text-white text-xs py-2 px-6 text-center">
          <Link to="/" className="hover:underline cursor-pointer">← Quay lại trang chủ Bizwell</Link>
        </div>
        <div className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-md text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-checkbox-circle-line text-4xl text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-[#004A99] mb-2">Đăng ký thành công!</h1>
            <p className="text-gray-500 text-sm mb-8">Chào mừng bạn đến với Bizwell · 탄소나노협회</p>

            <div className="bg-white border border-gray-200 rounded-2xl p-6 text-left space-y-4 mb-6">
              <div>
                <p className="text-xs text-gray-400 mb-1 font-medium">Mã khách hàng (M2 Code)</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-[#EDF2FB] text-[#004A99] font-mono font-bold text-base px-4 py-2.5 rounded-lg tracking-wider">
                    {generatedCode}
                  </code>
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1 font-medium">Mã đăng nhập</p>
                <code className="block bg-amber-50 text-amber-800 font-mono text-sm px-4 py-2.5 rounded-lg border border-amber-200">
                  {loginKey}
                </code>
                <p className="text-xs text-gray-400 mt-1.5">
                  = Số điện thoại + 3 ký tự đầu tên (viết thường)
                </p>
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-xs text-blue-700 leading-relaxed">
                <i className="ri-information-line mr-1" />
                <strong>Lưu ý:</strong> Khi đăng nhập, nhập số điện thoại và họ tên đầy đủ. Mã code trên chỉ dùng cho quản lý nội bộ.
              </div>
            </div>

            <div className="flex gap-3">
              <Link
                to="/login"
                className="flex-1 bg-[#004A99] hover:bg-[#003d7d] text-white py-3 rounded-xl font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-login-box-line" />
                Đăng Nhập Ngay
              </Link>
              <Link
                to="/"
                className="flex-1 border border-gray-200 hover:bg-gray-50 text-gray-600 py-3 rounded-xl font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-home-line" />
                Về Trang Chủ
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F9FA] via-white to-[#EDF2FB] flex flex-col">
      <div className="bg-[#004A99] text-white text-xs py-2 px-6 text-center">
        <Link to="/" className="hover:underline cursor-pointer">← Quay lại trang chủ Bizwell</Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-lg">
          {/* Header */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-3 cursor-pointer mb-5">
              <div className="w-10 h-10 rounded-xl bg-[#004A99] flex items-center justify-center">
                <span className="text-white font-black text-base">BW</span>
              </div>
              <span className="text-[#004A99] font-black text-xl tracking-tight">Bizwell</span>
            </Link>
            <h1 className="text-2xl font-bold text-gray-800 mt-2">Đăng Ký Thành Viên Miễn Phí</h1>
            <p className="text-sm text-gray-500 mt-1.5">
              Nhận thông báo sản phẩm mới & cơ hội hợp tác từ doanh nghiệp Hàn Quốc
            </p>

            <div className="flex items-center justify-center gap-6 mt-4">
              {["🆓 Hoàn toàn miễn phí", "🇰🇷 382+ doanh nghiệp HQ", "📦 999+ sản phẩm"].map((item) => (
                <span key={item} className="text-xs text-[#004A99] font-medium">{item}</span>
              ))}
            </div>
          </div>

          {/* Form */}
          <div className="bg-white rounded-2xl border border-gray-200 p-7">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-6 h-6 rounded-full bg-[#004A99] flex items-center justify-center">
                <span className="text-white text-xs font-bold">M2</span>
              </div>
              <span className="text-sm font-semibold text-gray-700">Thông tin đăng ký</span>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Họ và tên <span className="text-red-500">*</span>
                  <span className="ml-1 font-normal text-gray-400">(nhập đầy đủ họ tên)</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="Nguyễn Văn An"
                  className={`w-full px-3.5 py-2.5 text-sm border rounded-lg outline-none transition-all ${
                    (errors as Record<string, string>)["name"]
                      ? "border-red-300 focus:border-red-400"
                      : "border-gray-200 focus:border-[#004A99] focus:ring-1 focus:ring-[#004A99]/30"
                  }`}
                />
                {(errors as Record<string, string>)["name"] && (
                  <p className="text-xs text-red-500 mt-1">{(errors as Record<string, string>)["name"]}</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Số điện thoại <span className="text-red-500">*</span>
                  <span className="ml-1 font-normal text-gray-400">(dùng để đăng nhập)</span>
                </label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  placeholder="0938085313"
                  className={`w-full px-3.5 py-2.5 text-sm border rounded-lg outline-none transition-all ${
                    (errors as Record<string, string>)["phone"]
                      ? "border-red-300 focus:border-red-400"
                      : "border-gray-200 focus:border-[#004A99] focus:ring-1 focus:ring-[#004A99]/30"
                  }`}
                />
                {(errors as Record<string, string>)["phone"] && (
                  <p className="text-xs text-red-500 mt-1">{(errors as Record<string, string>)["phone"]}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Email
                  <span className="ml-1 font-normal text-gray-400">(để nhận thông báo & trả lời bình luận)</span>
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="email@example.com"
                  className={`w-full px-3.5 py-2.5 text-sm border rounded-lg outline-none transition-all ${
                    (errors as Record<string, string>)["email"]
                      ? "border-red-300 focus:border-red-400"
                      : "border-gray-200 focus:border-[#004A99] focus:ring-1 focus:ring-[#004A99]/30"
                  }`}
                />
                {(errors as Record<string, string>)["email"] && (
                  <p className="text-xs text-red-500 mt-1">{(errors as Record<string, string>)["email"]}</p>
                )}
              </div>

              {/* Company */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Tên công ty / doanh nghiệp</label>
                <input
                  type="text"
                  value={form.company}
                  onChange={(e) => update("company", e.target.value)}
                  placeholder="Công ty TNHH ABC"
                  className="w-full px-3.5 py-2.5 text-sm border border-gray-200 rounded-lg focus:border-[#004A99] focus:ring-1 focus:ring-[#004A99]/30 outline-none transition-all"
                />
              </div>

              {/* Industry + City */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Ngành nghề <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={form.industry}
                    onChange={(e) => update("industry", e.target.value)}
                    className={`w-full px-3 py-2.5 text-sm border rounded-lg outline-none transition-all cursor-pointer ${
                      (errors as Record<string, string>)["industry"]
                        ? "border-red-300"
                        : "border-gray-200 focus:border-[#004A99]"
                    }`}
                  >
                    <option value="">-- Chọn --</option>
                    {INDUSTRIES.map((ind) => (
                      <option key={ind} value={ind}>{ind}</option>
                    ))}
                  </select>
                  {(errors as Record<string, string>)["industry"] && (
                    <p className="text-xs text-red-500 mt-1">{(errors as Record<string, string>)["industry"]}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Thành phố <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={form.city}
                    onChange={(e) => update("city", e.target.value)}
                    className={`w-full px-3 py-2.5 text-sm border rounded-lg outline-none transition-all cursor-pointer ${
                      (errors as Record<string, string>)["city"]
                        ? "border-red-300"
                        : "border-gray-200 focus:border-[#004A99]"
                    }`}
                  >
                    <option value="">-- Chọn --</option>
                    {CITIES.map((city) => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                  {(errors as Record<string, string>)["city"] && (
                    <p className="text-xs text-red-500 mt-1">{(errors as Record<string, string>)["city"]}</p>
                  )}
                </div>
              </div>

              {/* Login Key Preview */}
              {form.phone && form.name && form.name.trim().split(" ").length >= 2 && (
                <div className="bg-[#EDF2FB] rounded-xl p-3.5 text-xs">
                  <p className="text-gray-500 mb-1">Mã đăng nhập của bạn sẽ là:</p>
                  <code className="text-[#004A99] font-mono font-bold text-base">
                    {generateLoginKey(form.phone, form.name)}
                  </code>
                  <p className="text-gray-400 mt-1">= SĐT + 3 ký tự đầu tên cuối (viết thường)</p>
                </div>
              )}

              {/* Terms */}
              <div>
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.agreeTerms}
                    onChange={(e) => update("agreeTerms", e.target.checked)}
                    className="mt-0.5 cursor-pointer"
                  />
                  <span className="text-xs text-gray-600 leading-relaxed">
                    Tôi đồng ý với{" "}
                    <span className="text-[#004A99] font-medium cursor-pointer">Điều khoản sử dụng</span>{" "}
                    và{" "}
                    <span className="text-[#004A99] font-medium cursor-pointer">Chính sách bảo mật</span>{" "}
                    của Bizwell
                  </span>
                </label>
                {(errors as Record<string, string>)["agreeTerms"] && (
                  <p className="text-xs text-red-500 mt-1 ml-5">{(errors as Record<string, string>)["agreeTerms"]}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#004A99] hover:bg-[#003d7d] text-white py-3.5 rounded-xl font-semibold text-sm transition-colors cursor-pointer disabled:opacity-60 whitespace-nowrap flex items-center justify-center gap-2 mt-2"
              >
                {loading ? (
                  <><i className="ri-loader-4-line animate-spin" /> Đang xử lý...</>
                ) : (
                  <><i className="ri-user-add-line" /> Đăng Ký Miễn Phí</>
                )}
              </button>

              <p className="text-center text-xs text-gray-500">
                Đã có tài khoản?{" "}
                <Link to="/login" className="text-[#004A99] font-semibold hover:underline cursor-pointer">
                  Đăng nhập ngay
                </Link>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
